import React, { useState, useRef, useCallback } from 'react';
import {
  Zap, FileText, Upload, X, RefreshCw, Copy,
  Download, Shield, Plus, CheckSquare,
  AlertCircle, Sparkles,
} from 'lucide-react';
import styles from '../styles/notebookAssignPanel.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const LEVELS = ['high_school', 'undergraduate', 'graduate', 'doctoral'];
const LEVEL_LABELS = { high_school: 'High School', undergraduate: 'Undergrad', graduate: 'Graduate', doctoral: 'Doctoral' };
const CITATIONS = ['APA', 'MLA', 'Chicago', 'Harvard', 'None'];
const VOICES = ['academic', 'analytical', 'persuasive', 'creative'];

const BUILD_PHASES = [
  { id: 'analyzing',  label: 'Analysing requirements' },
  { id: 'outlining',  label: 'Creating outline' },
  { id: 'drafting',   label: 'Writing draft' },
  { id: 'reviewing',  label: 'Reviewing & enhancing' },
  { id: 'citations',  label: 'Formatting citations' },
  { id: 'polishing',  label: 'Final polish' },
];

const NotebookAssignPanel = ({ activePage, onInsertContent }) => {
  // ─── Setup state ────────────────────────────────────────────────────
  const [prompt,       setPrompt]       = useState('');
  const [wordCount,    setWordCount]    = useState(1000);
  const [level,        setLevel]        = useState('undergraduate');
  const [citation,     setCitation]     = useState('APA');
  const [voice,        setVoice]        = useState('academic');
  const [useQuotes,    setUseQuotes]    = useState(true);
  const [references,   setReferences]   = useState([]);

  // ─── Generation state ───────────────────────────────────────────────
  const [step,         setStep]         = useState(1); // 1=setup 2=building 3=result
  const [phaseIdx,     setPhaseIdx]     = useState(0);
  const [phasesDone,   setPhasesDone]   = useState([]);
  const [content,      setContent]      = useState('');
  const [aiScore,      setAiScore]      = useState(null); // 0-1
  const [humanizing,   setHumanizing]   = useState(false);
  const [error,        setError]        = useState(null);
  const [resultTab,    setResultTab]    = useState('preview'); // 'preview' | 'raw'
  const [copied,       setCopied]       = useState(false);

  const fileInputRef  = useRef(null);

  // ─── File upload ────────────────────────────────────────────────────
  const handleFileUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';

    const formData = new FormData();
    files.forEach(f => formData.append('documents', f));

    try {
      const res  = await fetch(`${API_BASE}/api/parse_documents`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success && data.documents) {
        setReferences(prev => [...prev, ...data.documents]);
      }
    } catch {
      // silently ignore – user can still type a prompt
    }
  }, []);

  const removeRef = useCallback((idx) => {
    setReferences(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // ─── Generate ───────────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || prompt.length < 20) {
      setError('Please describe your assignment (at least 20 characters).');
      return;
    }

    setError(null);
    setStep(2);
    setPhaseIdx(0);
    setPhasesDone([]);
    setContent('');
    setAiScore(null);

    const delay = (ms) => new Promise(r => setTimeout(r, ms));

    const tick = async (idx, ms) => {
      setPhaseIdx(idx);
      await delay(ms);
      setPhasesDone(prev => [...prev, BUILD_PHASES[idx].id]);
    };

    try {
      await tick(0, 1200); // analysing
      await tick(1, 1400); // outlining

      // Start writing phase
      setPhaseIdx(2);

      const payload = {
        title: 'Assignment',
        requirements: prompt + (useQuotes ? '' : ' Do NOT include direct quotes.'),
        citationStyle: citation,
        voice,
        wordCount,
        academicLevel: level,
        includeOutline: true,
        useQuotes,
      };
      if (references.length > 0) payload.documents = references;

      const endpoint = references.length > 0
        ? '/api/generate_with_sources'
        : '/api/generate_assignment';

      const res  = await fetch(`${API_BASE}${endpoint}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Generation failed');

      let generated = data.content;
      setPhasesDone(prev => [...prev, BUILD_PHASES[2].id]);

      await tick(3, 1800); // reviewing
      await tick(4, 900);  // citations
      await tick(5, 700);  // polishing

      setContent(generated);

      // AI detection
      try {
        const detRes  = await fetch(`${API_BASE}/api/ai_detection`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ content: generated }),
        });
        const detData = await detRes.json();
        if (detData.success) setAiScore(detData.ai_detection?.ai_generated_percentage ?? null);
      } catch { /* optional */ }

      setStep(3);
    } catch (err) {
      setError(err.message || 'Something went wrong. Is the backend running?');
      setStep(1);
    }
  }, [prompt, wordCount, level, citation, voice, useQuotes, references]);

  // ─── Humanize ───────────────────────────────────────────────────────
  const handleHumanize = useCallback(async (strategy = 'balanced') => {
    if (!content) return;
    setHumanizing(true);
    setError(null);
    try {
      const endpoint = strategy === 'ultra' ? '/api/ultra_humanize' : '/api/humanize_content';
      const body     = strategy === 'ultra'
        ? { content }
        : { content, strategy, target_score: 0.25 };

      const res  = await fetch(`${API_BASE}${endpoint}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.humanized_content);
        if (data.ai_detection) setAiScore(data.ai_detection.ai_generated_percentage ?? null);
      } else {
        throw new Error(data.error || 'Humanization failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setHumanizing(false);
    }
  }, [content]);

  // ─── Insert into page ───────────────────────────────────────────────
  const handleInsert = useCallback(() => {
    if (content && onInsertContent) onInsertContent(content);
  }, [content, onInsertContent]);

  // ─── Copy ───────────────────────────────────────────────────────────
  const handleCopy = useCallback(() => {
    navigator.clipboard?.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [content]);

  // ─── Download ───────────────────────────────────────────────────────
  const handleDownload = useCallback(() => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'assignment.md';
    a.click();
    URL.revokeObjectURL(url);
  }, [content]);

  // ─── Reset ──────────────────────────────────────────────────────────
  const handleReset = () => {
    setStep(1);
    setContent('');
    setAiScore(null);
    setError(null);
    setPhasesDone([]);
  };

  const scoreColor = (s) => {
    if (s === null) return 'var(--text-tertiary)';
    if (s > 0.65) return '#ef4444';
    if (s > 0.35) return '#f59e0b';
    return '#10b981';
  };

  const scoreLabel = (s) => {
    if (s === null) return '—';
    if (s > 0.65) return 'High AI risk';
    if (s > 0.35) return 'Moderate risk';
    return 'Looks human';
  };

  // ═══════════════════════════════════════════════════════════════════
  //  STEP 1 – Setup
  // ═══════════════════════════════════════════════════════════════════
  if (step === 1) return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.panelHeader}>
        <Sparkles size={14} />
        <span>Assignment Builder</span>
      </div>

      <div className={styles.form}>
        {/* Prompt */}
        <div className={styles.field}>
          <label className={styles.label}>Assignment brief</label>
          <textarea
            className={styles.promptArea}
            rows={4}
            placeholder="Describe your assignment — topic, angle, any special instructions…"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
          />
          <div className={styles.charCount}>{prompt.length} chars</div>
        </div>

        {/* Word count */}
        <div className={styles.field}>
          <label className={styles.label}>Word count</label>
          <div className={styles.chips}>
            {[500, 750, 1000, 1500, 2000].map(w => (
              <button
                key={w}
                className={`${styles.chip} ${wordCount === w ? styles.chipActive : ''}`}
                onClick={() => setWordCount(w)}
              >{w}</button>
            ))}
          </div>
        </div>

        {/* Academic level */}
        <div className={styles.field}>
          <label className={styles.label}>Academic level</label>
          <div className={styles.chips}>
            {LEVELS.map(l => (
              <button
                key={l}
                className={`${styles.chip} ${level === l ? styles.chipActive : ''}`}
                onClick={() => setLevel(l)}
              >{LEVEL_LABELS[l]}</button>
            ))}
          </div>
        </div>

        {/* Citation + Voice row */}
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>Citation</label>
            <select className={styles.select} value={citation} onChange={e => setCitation(e.target.value)}>
              {CITATIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Voice</label>
            <select className={styles.select} value={voice} onChange={e => setVoice(e.target.value)}>
              {VOICES.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
        </div>

        {/* Quotes toggle */}
        <div className={styles.toggle} onClick={() => setUseQuotes(v => !v)}>
          <div className={`${styles.toggleBox} ${useQuotes ? styles.toggleOn : ''}`}>
            {useQuotes && <CheckSquare size={11} />}
          </div>
          <span>Include direct quotes</span>
        </div>

        {/* References */}
        <div className={styles.field}>
          <label className={styles.label}>Reference documents (optional)</label>
          <button
            className={styles.uploadBtn}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={13} />
            Upload PDF / DOCX / TXT
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.md"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          {references.length > 0 && (
            <div className={styles.refList}>
              {references.map((r, i) => (
                <div key={i} className={styles.refItem}>
                  <FileText size={11} />
                  <span>{r.filename || r.name || `Ref ${i + 1}`}</span>
                  <button className={styles.refRemove} onClick={() => removeRef(i)}>
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className={styles.error}>
            <AlertCircle size={13} />
            {error}
          </div>
        )}

        <button className={styles.generateBtn} onClick={handleGenerate}>
          <Zap size={14} />
          Build Assignment
        </button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════
  //  STEP 2 – Building
  // ═══════════════════════════════════════════════════════════════════
  if (step === 2) return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <RefreshCw size={14} className={styles.spin} />
        <span>Building…</span>
      </div>

      <div className={styles.buildPhases}>
        {BUILD_PHASES.map((ph, i) => {
          const done   = phasesDone.includes(ph.id);
          const active = phaseIdx === i && !done;
          return (
            <div key={ph.id} className={`${styles.phase} ${done ? styles.phaseDone : ''} ${active ? styles.phaseActive : ''}`}>
              <div className={styles.phaseIcon}>
                {done   ? <CheckSquare size={12} /> :
                 active ? <RefreshCw size={12} className={styles.spin} /> :
                          <div className={styles.phaseDot} />}
              </div>
              <span>{ph.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════
  //  STEP 3 – Result
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.panelHeader}>
        <Sparkles size={14} />
        <span>Assignment ready</span>
        <button className={styles.resetBtn} onClick={handleReset} title="Start over">
          <RefreshCw size={12} />
        </button>
      </div>

      {/* AI Detection badge */}
      {aiScore !== null && (
        <div className={styles.aiScore} style={{ '--score-color': scoreColor(aiScore) }}>
          <Shield size={12} />
          <span>AI detection: <strong>{Math.round(aiScore * 100)}%</strong></span>
          <span className={styles.scoreLabel}>{scoreLabel(aiScore)}</span>
        </div>
      )}

      {/* Humanize buttons */}
      <div className={styles.humanizeRow}>
        <span className={styles.humanizeLabel}>De-AI:</span>
        {['light', 'balanced', 'aggressive', 'ultra'].map(s => (
          <button
            key={s}
            className={styles.humanizeBtn}
            onClick={() => handleHumanize(s)}
            disabled={humanizing}
          >
            {humanizing ? <RefreshCw size={10} className={styles.spin} /> : null}
            {s}
          </button>
        ))}
      </div>

      {/* Result tabs */}
      <div className={styles.resultTabs}>
        <button
          className={`${styles.resultTab} ${resultTab === 'preview' ? styles.resultTabActive : ''}`}
          onClick={() => setResultTab('preview')}
        >Preview</button>
        <button
          className={`${styles.resultTab} ${resultTab === 'raw' ? styles.resultTabActive : ''}`}
          onClick={() => setResultTab('raw')}
        >Raw</button>
      </div>

      {/* Content */}
      <div className={styles.resultBody}>
        {resultTab === 'preview'
          ? <div className={styles.resultPreview}>{content}</div>
          : <textarea className={styles.resultRaw} value={content} onChange={e => setContent(e.target.value)} />
        }
      </div>

      {error && (
        <div className={styles.error}>
          <AlertCircle size={13} />
          {error}
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <button className={styles.actionBtnPrimary} onClick={handleInsert} title="Insert into current page">
          <Plus size={13} />
          Insert into page
        </button>
        <button className={styles.actionBtn} onClick={handleCopy} title="Copy">
          <Copy size={13} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button className={styles.actionBtn} onClick={handleDownload} title="Download .md">
          <Download size={13} />
        </button>
      </div>
    </div>
  );
};

export default NotebookAssignPanel;
